import { DEFAULT_PIPEEX_PROFILE_NAME } from '../../config/defaultPipeexProfile/defaultPipeexProfile';
import { logPcfSettingsDebug } from './pcfSettingsDebug';
import { SettingsType } from './pcfSettingsTypes';
import {
  normalizeScopedProfileName,
  normalizeSettingsType,
  parseJson,
} from './pcfSettingsSerialization';
import {
  ACTIVE_PROFILE_STORAGE_KEY,
  setDurableStateValue,
} from '../../lib/durableState/durableState';

function debugPcfSettings(message: string, payload?: unknown): void {
  logPcfSettingsDebug('pcfSettingsClient', message, payload);
}

export type ActivePipeexProfile = {
  name: string;
  settingsType: SettingsType;
};

function getLocalStorageRef(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function setActivePipeexProfile(
  name: string,
  settingsType: SettingsType = SettingsType.User
): void {
  const storage = getLocalStorageRef();
  if (!storage) {
    debugPcfSettings('[pcfSettingsClient] setActivePipeexProfile skipped: localStorage unavailable');
    return;
  }

  const normalizedSettingsType = normalizeSettingsType(settingsType, SettingsType.User);
  const normalizedName = normalizeScopedProfileName(
    name,
    normalizedSettingsType,
    DEFAULT_PIPEEX_PROFILE_NAME
  );
  const payload: ActivePipeexProfile = {
    name: normalizedName,
    settingsType: normalizedSettingsType,
  };

  const serialized = JSON.stringify(payload);
  setDurableStateValue(ACTIVE_PROFILE_STORAGE_KEY, serialized);
  debugPcfSettings('[pcfSettingsClient] setActivePipeexProfile', payload);
}

export function getActivePipeexProfile(): ActivePipeexProfile {
  const storage = getLocalStorageRef();
  if (!storage) {
    debugPcfSettings('[pcfSettingsClient] getActivePipeexProfile fallback: localStorage unavailable');
    return {
      name: DEFAULT_PIPEEX_PROFILE_NAME,
      settingsType: SettingsType.User,
    };
  }

  const raw = storage.getItem(ACTIVE_PROFILE_STORAGE_KEY);
  const parsed = parseJson<Partial<ActivePipeexProfile> | null>(
    raw,
    null
  );
  if (!parsed || typeof parsed !== 'object') {
    debugPcfSettings('[pcfSettingsClient] getActivePipeexProfile fallback: no persisted active profile', {
      raw,
    });
    return {
      name: DEFAULT_PIPEEX_PROFILE_NAME,
      settingsType: SettingsType.User,
    };
  }

  const normalizedProfile = {
    settingsType: normalizeSettingsType(parsed.settingsType, SettingsType.User),
    name: normalizeScopedProfileName(
      parsed.name,
      normalizeSettingsType(parsed.settingsType, SettingsType.User),
      DEFAULT_PIPEEX_PROFILE_NAME
    ),
  };
  debugPcfSettings('[pcfSettingsClient] getActivePipeexProfile loaded', {
    raw,
    normalizedProfile,
  });
  return normalizedProfile;
}
